import { useState, useRef, useEffect } from "react";
import { useVoiceVisualizer, VoiceVisualizer } from "react-voice-visualizer";

//tools
import cc from "classcat";

//styles
import styles from "./BottomControls.module.css";

//icons
import Microphone from "../../assets/microphone.svg";
import PlayerPlay from "../../assets/player-play.svg";
import PlayerRecord from "../../assets/player-record.svg";
import WifiLoading from "../../assets/wifi-loading.svg";
import Delete from "../../assets/delete.svg";

function BottomControls({ isConnected, onUserActivity }) {
	const [inputValue, setInputValue] = useState("");
	const [isRecording, setIsRecording] = useState(false);
	const [recordingError, setRecordingError] = useState(null);
	const [inputHeight, setInputHeight] = useState(50);
	const [isSending, setIsSending] = useState(false);

	const inputRef = useRef(null);
	const recorderControls = useVoiceVisualizer();

	const resizeInput = () => {
		const el = inputRef.current;
		if (el) {
			el.style.height = "auto";
			el.style.height = el.scrollHeight + "px";
			setInputHeight(el.scrollHeight);
		}
	};

	useEffect(() => {
		resizeInput();
	}, [inputValue]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Delete" && isRecording) {
				e.preventDefault();
				handleDeleteRecording();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isRecording]);

	const handleSendMessage = (
		text = null,
		audio_id = null,
		audio_url = null
	) => {
		setIsSending(true);
		let textMessage = "";

		if (typeof text === "string") {
			textMessage = text;
		} else {
			textMessage = inputValue.trim();
		}

		if (!textMessage || !isConnected) return;

		const params = { text: textMessage };

		if (audio_id) {
			params.audioId = audio_id;
		}

		if (audio_url) {
			params.audioUrl = audio_url;
		}

		__cng_websocket_manager.send_message(params);

		setInputValue("");
		setIsSending(false);
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSendMessage();
		}
	};

	const handleMicClick = async (e) => {
		e.preventDefault();

		if (isRecording) {
			try {
				await recorderControls.stopRecording();
			} catch (err) {
				setRecordingError("Failed to stop recording");
			}
			return;
		}

		setRecordingError(null);
		try {
			await recorderControls.startRecording();
			setIsRecording(true);
		} catch (err) {
			setRecordingError("Could not start recording");
		}
	};

	const handleAudioStop = async (audioBlob) => {
		try {
			setIsSending(true);
			const result = await __cng_websocket_manager.send_message_with_audio(
				inputValue,
				audioBlob
			);
			if (result && result.transcription && result.audio_id) {
				handleSendMessage(
					result.transcription,
					result.audio_id,
					result.upload_info?.ObjectURL
				);
			} else {
				setInputValue("");
			}
		} catch (e) {
			setRecordingError("Failed to send audio");
		} finally {
			setIsSending(false);
		}
		setIsRecording(false);
	};

	const handleDeleteRecording = async () => {
		if (isRecording) {
			try {
				await recorderControls.stopRecording();
				recorderControls.clear && recorderControls.clear();
				setIsRecording(false);
			} catch (err) {
				setRecordingError("Failed to stop recording");
			}
		}
	};

	useEffect(() => {
		if (!isRecording && recorderControls?.recordedBlob) {
			return;
		}
		if (isRecording && recorderControls?.recordedBlob) {
			handleAudioStop(recorderControls.recordedBlob);
			recorderControls.clear && recorderControls.clear();
		}
	}, [recorderControls?.recordedBlob, isRecording]);

	const getSendButtonIcon = (inputValue, isRecording) => {
		if (isSending) return WifiLoading;
		if (isRecording) return PlayerRecord;
		if (inputValue.trim()) return PlayerPlay;
		return Microphone;
	};

	const getSendButtonActions = (inputValue) => {
		let actions = {};

		if (isSending) return actions;

		if (!inputValue.trim()) {
			actions = {
				onClick: handleMicClick,
			};
		} else {
			actions = {
				onClick: handleSendMessage,
			};
		}

		return actions;
	};

	return (
		<>
			<div className={styles["bottom-controls-container"]}>
				<div
					className={cc([styles["message-input-container"], { [styles["recording"]]: isRecording }])}
					style={isRecording ? {} :{ "--input-height": `${inputHeight}px` }}>
					{isRecording ? (
						<VoiceVisualizer
							mainContainerClassName={styles["waveform-container"]}
							canvasContainerClassName={styles["waveform-container-canvas"]}
							controls={recorderControls}
							isControlPanelShown={false}
							isDefaultUIShown={false}
							height={`50px`}
							width={'100%'}
							backgroundColor={'transparent'}
							mainBarColor={'#111'}
							secondaryBarColor={'#111'}
						/>
					) : (
						<textarea
							ref={inputRef}
							placeholder='Message'
							className={styles["message-input"]}
							value={inputValue}
							onChange={(e) => {
								setInputValue(e.target.value);
								resizeInput();
								if (onUserActivity) {
									onUserActivity();
								}
							}}
							onKeyPress={handleKeyPress}
							disabled={!isConnected}
							rows={1}
						/>
					)}
				</div>
				<div className={styles["message-buttons-container"]}>
					{isRecording && <div 
						className={styles["message-delete-button"]}
						onClick={handleDeleteRecording}>
						<img src={Delete} />
					</div>}
					<div
						className={cc([
							styles["message-send-button"],
							{ [styles["recording"]]: isRecording && !isSending },
						])}
						{...getSendButtonActions(inputValue)}>
						<img src={getSendButtonIcon(inputValue, isRecording)} />
					</div>
				</div>
			</div>
			{recordingError && (
				<div className={styles["recording-error"]}>{recordingError}</div>
			)}
		</>
	);
}

export default BottomControls;
